import { Check, Eye, Notebook, Pencil, Plus, Trash } from "lucide-react"
import { Button } from "../ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { useEffect, useState } from "react";
import posts from "@/shared/api/posts";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { useSitemapAutoUpdate } from "@/hooks/useSitemapAutoUpdate";
import { useSitemapEventHandler } from "@/utils/sitemapEventHandler";


export const ArticleList = (props) => {
    const [articles, setArticles] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [verified, setVerified] = useState(undefined);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const navigate = useNavigate();
    const { updateSitemapAndPing } = useSitemapAutoUpdate();
    const sitemapEvents = useSitemapEventHandler(updateSitemapAndPing);

    const fetchArticles = async () => {
        setLoading(true);
        try {
            const res = await posts.getPosts(page, limit, verified);
            setArticles(res.posts ?? []);
        } catch (err) {
            console.error(err.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchArticles().catch(console.error);
    }, [page, limit, verified]);

    const handleCreateClick = () => {
        navigate('/admin/post');
    }

    const handleArticleClick = (slug, mode) => {
        if (mode === "edit") {
            navigate(`/admin/article/${slug}`);
        } else {
            navigate(`/article/${slug}`);
        }

    }

    const handleArticleDelete = async (id, title, slug) => {
        try {
            console.log('🗑️ Deleting article:', { id, title, slug });
            
            const res = await posts.deletePost(id);
            if (res.message === "Post deleted") {
                toast.success("Статья успешно удалена");
                
                // Автоматически обновляем sitemap после удаления статьи
                console.log('🔄 Auto-updating sitemap after article deletion:', slug);
                await sitemapEvents.onArticleDeleted({
                    title: title,
                    slug: slug
                });
                
                // Обновляем список статей
                fetchArticles().catch(console.error);
            } else {
                toast.error("Ошибка при удалении статьи");
            }
        } catch (error) {
            console.error('❌ Error deleting article:', error);
            toast.error("Ошибка при удалении статьи");
        }
    }

    const formatDate = (date) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' };
        return new Date(date).toLocaleDateString('ru-RU', options);
    }

    const toggleVerified = (id) => {
        setVerified(prev => {
            if (prev === undefined) {
                return true;
            } else if (prev === true) {
                return false;
            } else {
                return undefined;
            }
        });
    }

    const verifiedText = verified === undefined ? "Все" : verified ? "Опубликованные" : "Не опубликованные";
    const icon = verified === undefined ? <Eye className="h-4 w-4 mr-1" /> : verified ? <Check className="h-4 w-4 mr-1" /> : <Notebook className="h-4 w-4 mr-1" />;

    return (
        <div className="bg-card rounded-lg border p-6">
            <div className="flex justify-between items-center">
                <h2 className="my-4">Статьи</h2>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleVerified}
                    disabled={loading}
                >
                    {icon}
                    {verifiedText}
                </Button>
            </div>
            <div className="flex justify-between items-center mb-4">
                <Button onClick={handleCreateClick}>
                    <Plus className="h-4 w-4 mr-1" />
                    Добавить статью
                </Button>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="max-w-[100px]">Статус</TableHead>
                        <TableHead>Заголовок</TableHead>
                        <TableHead>Дата создания</TableHead>
                        <TableHead className="w-[100px]">Действия</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    { articles?.map(article => (
                        <TableRow>
                            <TableCell>{article.published ? "Опубликовано" : "Черновик"}</TableCell>
                            <TableCell>{article.title}</TableCell>
                            <TableCell>{formatDate(article.createdAt)}</TableCell>
                            <TableCell>
                                <div className="flex gap-2">  
                                    <Button variant="outline" size="sm" onClick={() => handleArticleClick(article.slug)}>
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => handleArticleClick(article.slug, "edit")}>
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="outline" size="sm">
                                                <Trash className="h-4 w-4" />
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Подтверждение действия</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    Вы уверены, что хотите удалить статью <strong>{article.title}</strong>?
                                                    Все данные будут удалены безвозвратно.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Отмена</AlertDialogCancel>
                                                <AlertDialogAction onClick={() => handleArticleDelete(article._id, article.title, article.slug)}>
                                                    Подтвердить
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className="flex justify-center mt-4">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={loading || page * limit >= articles.length}
                >
                    { "Загрузить еще"}
                </Button>
            </div>
        </div>
    )
}